<template>
  <div class="card">
    <div class="bg-dark card-header text-white">Academic Indirect Cost Distribution - Fund Transfer</div>
    <div class="card-body">
      <div class="row">
        <div class="col text-left">
          <button class="btn btn-primary" @click="onlyShowSelected=!onlyShowSelected" :disabled="isDownloading" type="button" style="margin-bottom: 8px;">{{ onlyShowSelected ? "Show All" : "Calculate Selected" }}</button>
        </div>
        <div v-if="onlyShowSelected" class="col d-print-none dropdown text-right">
            <button :disabled="isDownloading" aria-expanded="false" aria-haspopup="true"
                    class="btn btn-secondary dropdown-toggle" data-toggle="dropdown" style="width: 100px"
                    type="button">
                <span>Actions</span>
            </button>
            <div class="dropdown-menu">
              <a @click.prevent="downloadAICFundsTransferReport(true, true, true)" class="btn btn-outline-light dropdown-item text-left"
                href="javascript:void(0)" title="Generate Transfer Request Report">
                  <i class="fa fa-file-pdf"></i>Transfer Request Report
              </a>
              <a @click.prevent="downloadAICFundsTransferReport(false, true, false)" class="btn btn-outline-light dropdown-item text-left"
                href="javascript:void(0)" title="Generate Transfer Request Report">
                  <i class="fa fa-file-pdf"></i>Budget Entry Report
              </a>
            </div>
        </div>
      </div>
      <AppLoadingContainer :predicate="isLoading">
        <b-card v-for="section in sections" :key="section.name" no-body class="mb-1">
          <b-card-header header-tag="header" class="bg-grey pl-3 pr-3 text-white" role="tab" v-b-toggle="`${section.name}-section`" variant="info" @click="section.expanded=!section.expanded">
            <i :class="section.expanded ? 'fas fa-caret-up' : 'fas fa-caret-down'"></i> {{section.label}}
          </b-card-header>
          <b-collapse visible :id="`${section.name}-section`" role="tabpanel">
            <b-card-body>
              <FundTransferTable v-if="section.name === 'departments'"
                                  :id="section.name"
                                  :accounts="filteredTTIAccounts"
                                  :creditTableColumns="ttiAccountsCreditTableColumns"
                                  :debitTableColumns="ttiAccountsDebitTableColumns"
                                  title="IDC Credit to Department Accounts"
                                  :canEdit="canEdit"
                                  :onlyShowSelected="onlyShowSelected"
                                  :isMinuend="true"
                                  @selectAccount="toggleSelectAccount($event.selected, $event.id, section.name)"
                                  @selectAll="toggleSelectAll($event.selected, section.name)" />
              <FundTransferTable v-if="section.name === 'pis'"
                                  :id="section.name"
                                  :accounts="filteredDepartmentAccounts"
                                  :creditTableColumns="departmentAccountsCreditTableColumns"
                                  :debitTableColumns="departmentAccountsDebitTableColumns"
                                  title="IDC Credit to PI Accounts"
                                  :canEdit="canEdit"
                                  :onlyShowSelected="onlyShowSelected"
                                  :isMinuend="false"
                                  @selectAccount="toggleSelectAccount($event.selected, $event.id, section.name)"
                                  @selectAll="toggleSelectAll($event.selected, section.name)" />
              <div v-if="section.name === 'netTransfers'">
                <table class="table table-sm table-custom-hover">
                  <thead>
                    <tr style="text-align: center;">
                      <th style="width:42px;"><button class="btn btn-sm btn-primary" @click="resetHighlightAll()" type="button"><i class="fas fa-undo" style="margin-right: 0px;"></i></button></th>
                      <th>Net Transfer to</th>
                      <th>IDC Credit</th>
                      <th>-</th>
                      <th>PI IDC Credit</th>
                      <th>=</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="netTransfers.length > 0">
                      <tr v-for="netTransfer in netTransfers" @click="toggleHighlightAccount(netTransfer.account)" :class="netTransfer.highlighted ? 'tr-custom-highlight' : ''" style="text-align: center;" :key="netTransfer.account">
                        <td></td>
                        <td>{{ netTransfer.sl }}</td>
                        <td :class="netTransfer.highlighted ? 'td-custom-highlight-minuend' : ''">{{ netTransfer.idcCredit | currency }}</td>
                        <td> - </td>
                        <td :class="netTransfer.highlighted ? 'td-custom-highlight-subtrahend' : ''">{{ netTransfer.piidcCredit | currency }}</td>
                        <td> = </td>
                        <td :class="netTransfer.highlighted ? 'td-custom-highlight-difference' : ''">{{ netTransfer.amount | currency }}</td>
                      </tr>
                    </template>
                    <tr v-if="netTransfers.length === 0" style="text-align: center;background-color: rgba(0, 0, 0, 0.05);" class="no-hover"><td colspan="7">Select records from the Departments and PI sections to calculate Net Transfers</td></tr>
                  </tbody>
                </table>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </AppLoadingContainer>
    </div>
  </div>
</template>

<script>
import { currency } from "../../filters/Currency";
import AppLoadingContainer from "@/components/shared/AppLoadingContainer";
import FundTransferTable from "@/components/academicIndirectCost/FundTransferTable.vue";
import AcademicIndirectCostService from "../../services/AcademicIndirectCostService";
import {ToastType} from "@/services/models/shared/ToastType";

export default {
  components: {
    AppLoadingContainer,
    FundTransferTable
  },
  filters: {
      currency
  },
  props: {
    fiscalYear: {
      required: true,
      type: Number
    },
    payPeriodStartDate: {
      required: true,
      type: Date
    },
    canEdit: {
      required: true,
      type: Boolean
    }
  },
  data() {
    return {
      isDownloading: false,
      allAccounts: {
        ttiAccounts: [],
        departmentAccounts: []
      },
      sections: [
        {
          name: "departments",
          label: "Departments",
          expanded: true
        },
        {
          name: "pis",
          label: "PIs",
          expanded: true
        },
        {
          name: "netTransfers",
          label: "Net Transfers",
          expanded: true
        }
      ],
      isLoading: false,
      ttiAccountsDebitTableColumns: [
        {
          key: "gl",
          label: "TTI Account (GL)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "objectCode",
          label: "TTI Object Code",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "account",
          label: "TTI Account (SL-SA)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "idcCredit",
          label: "Amount",
          style: "text-align:end;",
          filter: "currency",
          isAmount: true
        }
      ],
      ttiAccountsCreditTableColumns: [
        {
          key: "departmentGL",
          label: "Department Account (GL)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "departmentObjectCode",
          label: "Department Object Code",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "departmentAccount",
          label: "Department Account (SL-SA)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "idcCredit",
          label: "Amount",
          style: "text-align:end;",
          filter: "currency",
          isAmount: true
        },
      ],
      departmentAccountsDebitTableColumns: [
        {
          key: "gl",
          label: "Department Account (GL)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "account",
          label: "Department Account (SL-SA)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "piidcCredit",
          label: "Amount",
          style: "text-align:end;",
          filter: "currency",
          isAmount: true
        }
      ],
      departmentAccountsCreditTableColumns: [
        {
          key: "pigl",
          label: "PI Account (GL)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "piAccount",
          label: "PI Account (SL-SA)",
          style: "text-align:center;",
          filter: null,
          isAmount: false
        },
        {
          key: "piidcCredit",
          label: "Amount",
          style: "text-align:end;",
          filter: "currency",
          isAmount: true
        },
      ],
      onlyShowSelected: false,
      isPerformingAction: false
    }
  },
  computed:{
    filteredTTIAccounts() {
      return this.allAccounts.ttiAccounts.filter((obj) => this.onlyShowSelected ? obj.selected : true);
    },

    filteredDepartmentAccounts() {
      return this.allAccounts.departmentAccounts.filter((obj) => this.onlyShowSelected ? obj.selected : true);
    },

    netTransfers() {
      return this.onlyShowSelected ? this.selectedNetTransfers : this.allNetTransfers;
    },

    selectedNetTransfers() {
      const selectedPIAccounts = Object.groupBy(this.allAccounts.departmentAccounts.filter(a => a.selected), ({ sl }) => sl);
      const selectedDepartmentAccounts = Object.groupBy(this.allAccounts.ttiAccounts.filter(a => a.selected), ({ departmentSL }) => departmentSL);
      const uniqueSLs = ([...new Set(Object.keys(selectedPIAccounts).concat(Object.keys(selectedDepartmentAccounts)))]);

      return this.netTransferCalculations(uniqueSLs, selectedPIAccounts, selectedDepartmentAccounts);
    },

    allNetTransfers() {
      const piAccounts = Object.groupBy(this.allAccounts.departmentAccounts, ({ sl }) => sl);
      const departmentAccounts = Object.groupBy(this.allAccounts.ttiAccounts, ({ departmentSL }) => departmentSL);
      const uniqueSLs = ([...new Set(Object.keys(piAccounts).concat(Object.keys(departmentAccounts)))]);

      return this.netTransferCalculations(uniqueSLs, piAccounts, departmentAccounts);
    }
  },
  methods: {
    /**
     * @description Loads the data from the API
     */
    loadData() {
        const vm = this;
        vm.isLoading = true;

        AcademicIndirectCostService.getTTIAndDepartmentAccounts({
          payPeriodStartDate: this.payPeriodStartDate
        })
            .then(response => {
                vm.allAccounts = {
                  ttiAccounts: response.ttiAccounts.map((a) => { return {...a, highlighted: false, selected: false, id: crypto.randomUUID(), account: a.sl !== null && a.sa !== null ? `${a.sl}-${a.sa}` : '', departmentAccount: a.departmentSL !== null && a.departmentSA !== null ? `${a.departmentSL}-${a.departmentSA}` : '' }}),
                  departmentAccounts: response.departmentAccounts.map((a) => { return {...a, highlighted: false, selected: false, id: crypto.randomUUID(), account: a.sl !== null && a.sa !== null ? `${a.sl}-${a.sa}` : '', piAccount: a.pisl !== null && a.pisa !== null ? `${a.pisl}-${a.pisa}` : '' }})
                };
            })
            .catch(() => {
                vm.redirectToError()
            })
            .finally(() => {
                vm.isLoading = false;
            })
    },

    netTransferCalculations(uniqueSLs, piAccounts, departmentAccounts) {
      const netTransfers = [];
      uniqueSLs.forEach(
        (sl) => {
          const piAccountsResult = Object.keys(piAccounts).length > 0 && piAccounts[sl]
                                    ? piAccounts[sl]
                                    : [];
          const departmentAccountsResult = Object.keys(departmentAccounts).length > 0 && departmentAccounts[sl]
                                              ? departmentAccounts[sl]
                                              : [];
          const piidcCreditSum = piAccountsResult.map((a) => { return a.piidcCredit; }).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
          const idcCreditSum = departmentAccountsResult.map((a) => { return a.idcCredit; }).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
          const highlighted = piAccountsResult.length > 0 ? piAccountsResult[0].highlighted : departmentAccountsResult.length > 0 ? departmentAccountsResult[0].highlighted : false;
          netTransfers.push({
            account: piAccountsResult.length > 0 ? piAccountsResult[0].account : departmentAccountsResult.length > 0 ? departmentAccountsResult[0].account : null,
            sl: sl === "null" ? null : sl,
            piidcCredit: piidcCreditSum,
            idcCredit: idcCreditSum,
            amount: idcCreditSum - piidcCreditSum,
            highlighted: highlighted
          });
        }
      )

      return netTransfers;
    },

    toggleSelectAccount(selected, id, section) {
      if (section === "departments") {
        const index = this.allAccounts.ttiAccounts.findIndex(obj => {
          return obj.id === id
        });
        this.allAccounts.ttiAccounts[index].selected = selected;
      } else {
        const index = this.allAccounts.departmentAccounts.findIndex(obj => {
          return obj.id === id
        });
        this.allAccounts.departmentAccounts[index].selected = selected;
      }
    },

    toggleSelectAll(selected, section) {
      if (section === "departments") this.allAccounts.ttiAccounts = this.allAccounts.ttiAccounts.map(obj => { return {...obj, selected: selected } });
      else this.allAccounts.departmentAccounts = this.allAccounts.departmentAccounts.map(obj => { return {...obj, selected: selected } });
    },
    
    toggleHighlightAccount(account) {
      this.allAccounts.ttiAccounts.forEach((obj, index) => {
        if (obj.departmentAccount === account) this.allAccounts.ttiAccounts[index].highlighted = !this.allAccounts.ttiAccounts[index].highlighted;
        else this.allAccounts.ttiAccounts[index].highlighted = false;
      });
      this.allAccounts.departmentAccounts.forEach((obj, index) => {
        if (obj.account === account) this.allAccounts.departmentAccounts[index].highlighted = !this.allAccounts.departmentAccounts[index].highlighted;
        else this.allAccounts.departmentAccounts[index].highlighted = false;
      });
    },

    resetHighlightAll() {
      this.allAccounts.ttiAccounts = this.allAccounts.ttiAccounts.map(obj => { return { ...obj, highlighted: false }});
      this.allAccounts.departmentAccounts = this.allAccounts.departmentAccounts.map(obj => { return { ...obj, highlighted: false }});
    },

    /**
     * @description Downloads the aic funds transfer report
     */
    downloadAICFundsTransferReport(includeDepartments, includePIs, includeNetTransfers) {
      this.isDownloading = true;
      this.isLoading = true;

      const data = JSON.stringify({
        fiscalYear: this.fiscalYear,
        payPeriod: this.payPeriodStartDate,
        preparedBy: this.$store.state.user.name,
        ttiAccounts: includeDepartments ? this.allAccounts.ttiAccounts.filter(a => a.selected) : [],
        departmentAccounts: includePIs ? this.allAccounts.departmentAccounts.filter(a => a.selected) : [],
        netTransfers: includeNetTransfers ? this.selectedNetTransfers : []
      });
      AcademicIndirectCostService.downloadAICFundsTransferReport(this.payPeriodStartDate, data)
          .catch(error => {
              this.alert(ToastType.Danger, error.response.statusText)
          })
          .finally(() => {
            this.isDownloading = false;
            this.isLoading = false;
          });
    }
  },
  created() {
    this.loadData();
  }
}
</script>

<style>
table.table-custom-hover tbody tr:hover:not(.no-hover),
table tbody tr.tr-custom-highlight,
table tbody tr td.td-custom-highlight {
  background-color: #500000!important;
  color: white;
}
table tbody tr td.td-custom-highlight-minuend {
  background-color: #00500d!important;
  color: white;
}
table tbody tr td.td-custom-highlight-subtrahend {
  background-color: #a10000!important;
  color: white;
}
table tbody tr td.td-custom-highlight-difference {
  background-color: #006ea1!important;
  color: white;
}
table.table-custom-hover tbody tr.no-hover {
  background-color: rgba(0, 0, 0, 0.05)!important;
}
.bg-grey {
  background-color: rgba(0,0,0,0.50) !important
}
</style>